import { analytics } from './segment';

/**
 * Which tutorial flow the user is in:
 * - `new_user_keep_exploring`: Exp 242 new user tapped "Keep exploring" on the
 *   first-order screen.
 * - `new_user_edit_order`: Exp 242 new user tapped "Edit order" on the
 *   first-order screen.
 * - `existing_user_migration`: Exp 242, auto-triggered on the first eligible
 *   Home visit after enrollment — no entry button tap.
 * - `guest_feed_onboarding`: Exp 255 guest (authenticated, pre-checkout),
 *   auto-triggered on the first `/discover/feed/` visit — no entry button tap.
 */
export type TutorialFlowName =
  | 'new_user_keep_exploring'
  | 'new_user_edit_order'
  | 'existing_user_migration'
  | 'guest_feed_onboarding';

/**
 * The flows that open without an entry tap. These fire
 * {@link trackTutorialFlowStarted} when the overlay appears; the new-user
 * flows enter via `Button Clicked` (`keep_exploring` / `edit_order`) instead.
 */
export const AUTO_OPEN_TUTORIAL_FLOWS: ReadonlySet<TutorialFlowName> =
  new Set<TutorialFlowName>([
    'existing_user_migration',
    'guest_feed_onboarding',
  ]);

/**
 * In-flow forward-navigation buttons: `explore_the_new_look` is the
 * existing-user flow's first-screen CTA; `tutorial_continue` covers every
 * other intermediate screen's CTA.
 */
export type TutorialNavButtonName =
  | 'explore_the_new_look'
  | 'tutorial_continue';

const COMPONENT_TYPE = 'tutorial_flow';

/**
 * Entry event for the new-user tutorial flows: fires on the first-order
 * screen's CTA tap ("Keep exploring" / "Edit order"), before the tour overlay
 * appears. Fired alongside the legacy `Onboarding Button Clicked` event, which
 * stays untouched for existing dashboards. The existing-user flow has no entry
 * tap — it enters via `trackTutorialFlowStarted` instead.
 */
export function trackTutorialEntryClicked({
  buttonName,
}: {
  buttonName: 'keep_exploring' | 'edit_order';
}) {
  analytics.track('Button Clicked', {
    event_name: 'button_clicked',
    button_name: buttonName,
    client: 'web',
  });
}

type TutorialFlowScreenPosition = {
  /** 1-indexed screen position within the flow. */
  objectIndex: number;
  /** Total screen count for this flow. */
  objectTotal: number;
};

/**
 * Entry event for the auto-open flows ({@link AUTO_OPEN_TUTORIAL_FLOWS}):
 * fires when the overlay appears. New-user flows enter via a `Button Clicked`
 * (`keep_exploring` / `edit_order`) instead — do not fire this for them. Each
 * flow occurs at most once per user per client, so downstream queries join on
 * `user_id`; `tab` / `tab_session_id` are intentionally omitted.
 */
export function trackTutorialFlowStarted({
  flow,
  objectTotal,
}: Pick<TutorialFlowScreenPosition, 'objectTotal'> & {
  flow: TutorialFlowName;
}) {
  analytics.track('UX Component Interaction', {
    component_type: COMPONENT_TYPE,
    action_type: 'started',
    component_name: flow,
    object_index: 1,
    object_total: objectTotal,
    client: 'web',
  });
}

/**
 * Fires when the user taps the background to exit the tutorial overlay.
 * `objectIndex` records the screen they were on, enabling per-screen drop-off
 * analysis.
 */
export function trackTutorialFlowDismissed({
  flow,
  objectIndex,
  objectTotal,
}: TutorialFlowScreenPosition & { flow: TutorialFlowName }) {
  analytics.track('UX Component Interaction', {
    component_type: COMPONENT_TYPE,
    action_type: 'dismissed',
    component_name: flow,
    object_index: objectIndex,
    object_total: objectTotal,
    client: 'web',
  });
}

/**
 * Fires on each forward tap inside the tutorial overlay. `objectIndex`
 * identifies which screen the tap came from.
 */
export function trackTutorialNavClicked({
  buttonName,
  objectIndex,
  objectTotal,
}: TutorialFlowScreenPosition & { buttonName: TutorialNavButtonName }) {
  analytics.track('Button Clicked', {
    event_name: 'button_clicked',
    button_name: buttonName,
    object_index: objectIndex,
    object_total: objectTotal,
    client: 'web',
  });
}

/**
 * Completion signal: the user tapped the final screen's closing CTA
 * ("Ok, got it" on the new-user flows; the profile coachmark's dismiss on the
 * existing-user flow). `objectIndex` is the final screen (= objectTotal).
 */
export function trackTutorialOkGotItClicked({
  objectIndex,
  objectTotal,
}: TutorialFlowScreenPosition) {
  analytics.track('Button Clicked', {
    event_name: 'button_clicked',
    button_name: 'tutorial_ok_got_it',
    object_index: objectIndex,
    object_total: objectTotal,
    client: 'web',
  });
}
